import AccountBalanceRoundedIcon from '@mui/icons-material/AccountBalanceRounded';
import CheckCircleOutlineRoundedIcon from '@mui/icons-material/CheckCircleOutlineRounded';
import HomeRoundedIcon from '@mui/icons-material/HomeRounded';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import { Box, Chip, Stack, Typography } from '@mui/material';
import { alpha, useTheme } from '@mui/material/styles';
import type { FusedCapacityDto, FusedCapacityLedgerLineDto } from '../../api/admin';
import { ApplicationSectionCard } from '../application/applicationSectionChrome';

type Props = {
  fusedCapacity?: FusedCapacityDto | null;
};

function formatMoney(value?: number | null): string | null {
  if (typeof value !== 'number' || !Number.isFinite(value)) return null;
  return new Intl.NumberFormat('en-CA', {
    style: 'currency',
    currency: 'CAD',
    maximumFractionDigits: 0,
  }).format(value);
}

/** Cents → dollars, formatted as CAD. */
function moneyC(cents?: number | null): string | null {
  if (typeof cents !== 'number' || !Number.isFinite(cents)) return null;
  return formatMoney(cents / 100);
}

/** Cents → "$X/mo". */
function moneyMoC(cents?: number | null): string | null {
  const m = moneyC(cents);
  return m == null ? null : `${m}/mo`;
}

function humanize(token?: string | null): string {
  if (!token) return '—';
  return token.replace(/_/g, ' ').toLowerCase();
}

/** Friendly labels for the review-reason enum from the fused decision. */
const REVIEW_REASON_LABELS: Record<string, string> = {
  OVER_TDS_FROM_EXISTING_DEBT: 'Over TDS from existing debt',
  BUREAU_INVISIBLE_DEBT: 'Bureau-invisible debt',
  PAYDAY_STACKING: 'Payday stacking',
  DEBT_PAYMENT_NSF: 'Debt-payment NSF',
  TU_FEED_UNAVAILABLE: 'TransUnion feed unavailable — verify',
};
function reviewReasonLabel(reason: string): string {
  if (REVIEW_REASON_LABELS[reason]) return REVIEW_REASON_LABELS[reason];
  // Fallback for any new reason: humanize then capitalize the first letter.
  const h = humanize(reason);
  return h.charAt(0).toUpperCase() + h.slice(1);
}

/** Shelter (housing) categories folded into TDS — rendered with a home marker. */
const SHELTER_CATEGORIES = new Set(['rent', 'mortgage', 'property_tax']);
function isShelter(category?: string | null): boolean {
  return typeof category === 'string' && SHELTER_CATEGORIES.has(category.toLowerCase());
}
function categoryLabel(category?: string | null): string {
  if (!category) return '—';
  if (category.toLowerCase() === 'property_tax') return 'Property tax';
  const h = humanize(category);
  return h.charAt(0).toUpperCase() + h.slice(1);
}

function sourceChip(source: FusedCapacityLedgerLineDto['source']) {
  // Bank-only = our incremental value (success); TU-only = bureau-only (info); Both = neutral.
  if (source === 'BANK_ONLY') {
    return <Chip size="small" color="success" label="Bank only" sx={{ height: 20, fontWeight: 700, fontSize: '0.65rem' }} />;
  }
  if (source === 'TU_ONLY') {
    return <Chip size="small" color="info" label="TU only" sx={{ height: 20, fontWeight: 700, fontSize: '0.65rem' }} />;
  }
  return <Chip size="small" variant="outlined" label="Both" sx={{ height: 20, fontWeight: 700, fontSize: '0.65rem' }} />;
}

function decisionChip(decision: FusedCapacityDto['decision']) {
  if (decision === 'APPROVE') {
    return (
      <Chip
        icon={<CheckCircleOutlineRoundedIcon />}
        size="small"
        color="success"
        label="Approve"
        sx={{ height: 22, fontWeight: 700 }}
      />
    );
  }
  return (
    <Chip
      icon={<InfoOutlinedIcon />}
      size="small"
      color="warning"
      label="Review"
      sx={{ height: 22, fontWeight: 700 }}
    />
  );
}

type KvField = { label: string; value?: string | number | null; emphasize?: boolean; always?: boolean };

function visibleKvRows(rows: KvField[]): KvField[] {
  return rows
    .map((r) => {
      if (r.value != null && r.value !== '') return r;
      if (r.always) return { ...r, value: '—' };
      return null;
    })
    .filter((r): r is KvField => r != null);
}

/** Two-column key/value block — mirrors the Decision Engine panel's KvSection. */
function KvSection({ title, subtitle, rows }: { title: string; subtitle?: string; rows: KvField[] }) {
  const visible = visibleKvRows(rows);
  if (visible.length === 0) return null;
  return (
    <Box
      sx={{
        minWidth: 0,
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
        gap: 0.85,
        p: 1.5,
        borderRadius: 1.5,
        border: '1px solid',
        borderColor: 'divider',
        bgcolor: 'background.paper',
      }}
    >
      <Box>
        <Typography
          variant="caption"
          sx={{
            fontWeight: 800,
            letterSpacing: '0.05em',
            textTransform: 'uppercase',
            color: 'text.primary',
            fontSize: '0.65rem',
            display: 'block',
          }}
        >
          {title}
        </Typography>
        {subtitle ? (
          <Typography
            variant="caption"
            display="block"
            sx={{ mt: 0.2, lineHeight: 1.4, color: 'text.primary', fontWeight: 500, opacity: 0.78 }}
          >
            {subtitle}
          </Typography>
        ) : null}
      </Box>
      <Box component="dl" sx={{ m: 0, flex: 1 }}>
        {visible.map((row, i) => (
          <Box
            key={row.label}
            sx={{
              display: 'flex',
              alignItems: 'baseline',
              justifyContent: 'space-between',
              columnGap: 2,
              rowGap: 0.25,
              py: 0.8,
              borderTop: i === 0 ? undefined : '1px solid',
              borderColor: 'divider',
            }}
          >
            <Typography
              component="dt"
              sx={{ flex: '1 1 auto', minWidth: 0, fontSize: '0.8125rem', fontWeight: 600, color: 'text.primary', lineHeight: 1.4, opacity: 0.9 }}
            >
              {row.label}
            </Typography>
            <Typography
              component="dd"
              variant="body2"
              sx={{
                m: 0,
                flex: '0 1 auto',
                maxWidth: '58%',
                textAlign: 'right',
                fontWeight: row.emphasize && row.value !== '—' ? 700 : 600,
                fontSize: row.emphasize && row.value !== '—' ? '0.95rem' : '0.875rem',
                color: row.value === '—' ? 'text.secondary' : 'text.primary',
                lineHeight: 1.45,
                wordBreak: 'break-word',
                fontVariantNumeric: 'tabular-nums',
              }}
            >
              {row.value}
            </Typography>
          </Box>
        ))}
      </Box>
    </Box>
  );
}

function TableHeaderCell({ children, align }: { children: string; align?: 'right' | 'left' }) {
  return (
    <Typography
      component="th"
      variant="caption"
      sx={{
        fontWeight: 800,
        letterSpacing: '0.07em',
        textTransform: 'uppercase',
        color: 'text.secondary',
        fontSize: '0.65rem',
        textAlign: align ?? 'left',
        py: 1,
        px: 1.25,
        whiteSpace: 'nowrap',
      }}
    >
      {children}
    </Typography>
  );
}

export function ApplicationFusedCapacityPanel({ fusedCapacity }: Props) {
  const theme = useTheme();

  if (!fusedCapacity || !Array.isArray(fusedCapacity.ledger)) {
    return null; // nothing to show unless fused capacity ran
  }

  const ledger = fusedCapacity.ledger;
  const bankOnlyCount = ledger.filter((l) => l.source === 'BANK_ONLY').length;
  const isReview = fusedCapacity.decision === 'REVIEW';
  // Shelter (mortgage + rent + property tax) folded into the debt-service total.
  const housingCents = ledger
    .filter((l) => l.counted && isShelter(l.category))
    .reduce((s, l) => s + (l.monthlyAmountCents ?? 0), 0);

  const outcomeRows: KvField[] = [
    { label: 'Final offer', value: moneyC(fusedCapacity.finalOfferCents), emphasize: true, always: true },
    { label: 'Max eligible (fused)', value: moneyC(fusedCapacity.maxEligibleAmountCents), always: true },
    { label: 'Bureau max (DE)', value: moneyC(fusedCapacity.deBureauMaxCents), always: true },
    { label: 'Requested', value: moneyC(fusedCapacity.requestedCents), always: true },
    { label: 'Bound by', value: humanize(fusedCapacity.bindingConstraint), always: true },
  ];

  const debtRows: KvField[] = [
    { label: 'Existing debt service', value: moneyMoC(fusedCapacity.fusedMonthlyDebtServiceCents), emphasize: true, always: true },
    { label: 'of which housing (shelter)', value: housingCents > 0 ? moneyMoC(housingCents) : 'None' },
    { label: 'Available for new payment', value: moneyMoC(fusedCapacity.availableForNewPaymentCents), always: true },
    {
      label: 'Advisory (not counted)',
      value:
        typeof fusedCapacity.advisoryMonthlyDebtServiceCents === 'number'
          ? moneyMoC(fusedCapacity.advisoryMonthlyDebtServiceCents)
          : null,
      always: true,
    },
    {
      label: 'Caught, not on TransUnion',
      value: bankOnlyCount > 0 ? `${bankOnlyCount} obligation${bankOnlyCount === 1 ? '' : 's'}` : 'None',
      always: true,
    },
  ];

  return (
    <ApplicationSectionCard
      title="Fused Capacity"
      subtitle="Bank-derived obligations + TransUnion, reconciled into the offer"
      icon={<AccountBalanceRoundedIcon fontSize="small" />}
      iconColor={isReview ? 'warning' : 'success'}
      data-testid="application-fused-capacity-panel"
      endAdornment={decisionChip(fusedCapacity.decision)}
    >
      <Stack spacing={1.5}>
        <Box
          sx={{
            display: 'grid',
            gap: 1.5,
            gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' },
            alignItems: 'stretch',
          }}
        >
          <KvSection
            title="Capacity outcome"
            subtitle="Tightest of our capacity, the bureau max, and the request"
            rows={outcomeRows}
          />
          <KvSection
            title="Debt & affordability"
            subtitle="After the obligations we detected from the bank"
            rows={debtRows}
          />
        </Box>

        {/* Review reasons */}
        {isReview && fusedCapacity.reviewReasons.length > 0 ? (
          <Box
            sx={{
              px: 1.5,
              py: 1.25,
              borderRadius: 1.5,
              border: '1px solid',
              borderColor: alpha(theme.palette.warning.main, 0.4),
              borderLeftWidth: 3,
              borderLeftColor: 'warning.main',
              bgcolor: 'background.paper',
            }}
          >
            <Typography
              variant="caption"
              sx={{
                fontWeight: 800,
                letterSpacing: '0.05em',
                textTransform: 'uppercase',
                color: 'text.primary',
                fontSize: '0.65rem',
                display: 'block',
                mb: 0.75,
              }}
            >
              Routed to review
            </Typography>
            <Stack direction="row" spacing={0.75} flexWrap="wrap" useFlexGap>
              {fusedCapacity.reviewReasons.map((r) => (
                <Chip
                  key={r}
                  size="small"
                  color="warning"
                  variant="outlined"
                  label={reviewReasonLabel(r)}
                  sx={{ height: 22, fontWeight: 700 }}
                />
              ))}
            </Stack>
          </Box>
        ) : null}

        {/* Obligation ledger */}
        {ledger.length > 0 ? (
          <Box
            sx={{
              borderRadius: 1.5,
              border: '1px solid',
              borderColor: 'divider',
              bgcolor: 'background.paper',
              px: 1.5,
              py: 1.25,
            }}
          >
            <Typography
              variant="caption"
              sx={{
                fontWeight: 800,
                letterSpacing: '0.06em',
                textTransform: 'uppercase',
                color: 'text.secondary',
                fontSize: '0.65rem',
                display: 'block',
                mb: 1,
              }}
            >
              Obligation ledger
            </Typography>
            <Box sx={{ borderRadius: 1.5, border: '1px solid', borderColor: 'divider', overflow: 'hidden' }}>
              <Box sx={{ overflowX: 'auto' }}>
                <Box component="table" sx={{ width: '100%', minWidth: 560, borderCollapse: 'collapse' }}>
                  <Box
                    component="thead"
                    sx={{
                      bgcolor:
                        theme.palette.mode === 'light'
                          ? alpha(theme.palette.common.black, 0.02)
                          : alpha(theme.palette.common.white, 0.04),
                      borderBottom: '1px solid',
                      borderColor: 'divider',
                    }}
                  >
                    <Box component="tr">
                      <TableHeaderCell>Lender</TableHeaderCell>
                      <TableHeaderCell>Source</TableHeaderCell>
                      <TableHeaderCell>Type</TableHeaderCell>
                      <TableHeaderCell align="right">Monthly</TableHeaderCell>
                      <TableHeaderCell>Basis</TableHeaderCell>
                      <TableHeaderCell>Conf.</TableHeaderCell>
                    </Box>
                  </Box>
                  <Box component="tbody">
                    {ledger.map((line, idx) => (
                      <Box
                        component="tr"
                        key={`${line.lender}-${idx}`}
                        sx={{
                          borderBottom: idx < ledger.length - 1 ? '1px solid' : undefined,
                          borderColor: 'divider',
                          opacity: line.counted ? 1 : 0.55,
                          '&:hover': {
                            bgcolor: alpha(
                              theme.palette.common.black,
                              theme.palette.mode === 'light' ? 0.02 : 0.06,
                            ),
                          },
                        }}
                      >
                        <Typography
                          component="td"
                          variant="body2"
                          sx={{ py: 1, px: 1.25, fontWeight: 600, fontSize: '0.875rem' }}
                        >
                          {line.lender}
                          {line.tuTradelineCount && line.tuTradelineCount > 1 ? (
                            <Typography component="span" variant="caption" color="text.secondary">
                              {` ×${line.tuTradelineCount}`}
                            </Typography>
                          ) : null}
                          {!line.counted ? (
                            <Typography component="span" variant="caption" color="text.secondary">
                              {' (advisory)'}
                            </Typography>
                          ) : null}
                        </Typography>
                        <Box component="td" sx={{ py: 1, px: 1.25 }}>
                          {sourceChip(line.source)}
                        </Box>
                        <Box component="td" sx={{ py: 1, px: 1.25 }}>
                          <Stack direction="row" alignItems="center" spacing={0.5}>
                            {isShelter(line.category) ? (
                              <HomeRoundedIcon sx={{ fontSize: 15, color: 'text.secondary', flexShrink: 0 }} />
                            ) : null}
                            <Typography variant="body2" sx={{ fontSize: '0.8125rem', whiteSpace: 'nowrap' }}>
                              {categoryLabel(line.category)}
                            </Typography>
                          </Stack>
                        </Box>
                        <Typography
                          component="td"
                          variant="body2"
                          sx={{
                            py: 1,
                            px: 1.25,
                            textAlign: 'right',
                            fontWeight: 600,
                            fontSize: '0.875rem',
                            whiteSpace: 'nowrap',
                            fontVariantNumeric: 'tabular-nums',
                          }}
                        >
                          {moneyMoC(line.monthlyAmountCents) ?? '—'}
                        </Typography>
                        <Typography
                          component="td"
                          variant="caption"
                          sx={{ py: 1, px: 1.25, color: 'text.secondary', whiteSpace: 'nowrap', textTransform: 'capitalize' }}
                        >
                          {humanize(line.basis)}
                        </Typography>
                        <Typography
                          component="td"
                          variant="caption"
                          sx={{ py: 1, px: 1.25, color: 'text.secondary' }}
                        >
                          {line.confidence}
                        </Typography>
                      </Box>
                    ))}
                  </Box>
                </Box>
              </Box>
            </Box>
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1, lineHeight: 1.5 }}>
              Amount basis: <em>TU contractual</em> when the bureau reports a payment; <em>bank observed</em>{' '}
              when only the bank shows it; <em>discrepancy</em> uses the higher of the two. Advisory
              (low-confidence, bank-only) lines are shown but not deducted from capacity.{' '}
              <HomeRoundedIcon sx={{ fontSize: 13, verticalAlign: 'text-bottom', color: 'text.secondary' }} />{' '}
              <em>housing</em> (rent, mortgage, property tax) is shelter cost — it counts toward total
              debt service but is not treated as bureau-invisible distress debt.
            </Typography>
          </Box>
        ) : null}
      </Stack>
    </ApplicationSectionCard>
  );
}
